import {
  COUNTDOWN_MIN_DAYS,
  KitShape,
  MOTION_ARTWORK_LENGTHS,
  MotionArtworkLengthSec,
  needsFullVideoPlan,
  runCredits,
  StudioFormat,
  StudioMoment,
  StudioTheme,
  StudioThemeId,
  STUDIO_THEMES,
  themeOutputs,
  themesOfFormat,
} from 'fym-shared/src/music24/studioThemes';
import { isValidTrim, KitAssetTrim } from 'fym-shared/src/music24/fullVideoPlan';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { assetUsesInstructions, defaultAssetInstructions } from 'fym-shared/src/music24/assetInstructions';
import {
  AssetStyleId,
  defaultStyleFor,
  resolveAssetStyle,
  ResolvedAssetStyle,
  WaveformShape,
} from 'fym-shared/src/music24/assetStyles';
import {
  SPOTIFY_HEADER_DEFAULT_FOCUS,
  SPOTIFY_HEADER_DEFAULT_SOURCE,
  spotifyHeaderSourceNeeds,
  type SpotifyHeaderSource,
} from 'fym-shared/src/music24/spotifyHeader';
import { CreateKitAssetItem, KitAssetParams } from '../../../../redux/api/fymAPI.types';
import { isTrimmed } from './fullVideoRules';
import { daysUntilRelease } from '../releases/releaseOrder';

/** The song facts that turn a theme on or off. */
export interface StudioSongContext {
  /** The master of the catalog row is `ready`. */
  masterReady: boolean;
  /** Whole days to the release date; null with no date. 0 is the release day, below 0 is past. */
  daysToRelease: number | null;
  canUseAiReels: boolean;
  hasLyrics: boolean;
  /**
   * The plan makes full-song videos and the motion artwork (`canRenderFullSongVideos` of the credits
   * answer). Absent while the credits load: the cards stay open and the server decides.
   */
  canRenderFullSongVideos?: boolean;
}

/**
 * What a theme card shows (designs l2, n6, s7c): open, or the state that keeps it shut. A
 * `needsPlan` card (Free, a full video or the motion artwork) still opens step 3, but "Make it"
 * stays off.
 */
export type StudioThemeState = 'open' | 'needsMaster' | 'needsReleaseDate' | 'needsLyrics' | 'needsPlan';

/** The step-3 values the Studio keeps in memory. */
export interface StudioDetails {
  moment?: StudioMoment;
  lyricLine?: string;
  windowStartSec?: number;
  lengthSec?: KitAssetParams['lengthSec'];
  shape?: KitShape;
  /** The style the artist picked. Absent: the default style of the song's theme. */
  style?: AssetStyleId;
  /** The shape of the Waveform style. */
  waveform?: WaveformShape;
  /** The Instructions that the artist edited. Absent: the default text (it follows the style and the moment). */
  instructions?: string;
  /** A full video: the part of the song. Absent: the whole song. */
  trim?: KitAssetTrim;
  /** A lyric moment: the first and the last line index of the lyric timing. */
  lineFrom?: number;
  lineTo?: number;
  /** A Spotify header (spec 2026-10-17 §2.2): the picture and its focus. Absent: the blurred cover. */
  headerSource?: SpotifyHeaderSource;
  headerFileId?: number;
  headerPictureUuid?: string;
  focusX?: number;
  focusY?: number;
  /** A Spotify header photo: the address of the file, for the preview only (never sent). */
  headerPhotoUrl?: string;
}

/** The countdown days, newest last (design n3b). */
export const COUNTDOWN_DAYS: ReadonlyArray<7 | 3 | 1> = [7, 3, 1];

/**
 * Whole days from today to `releaseDate` (an ISO string). Null with no date. One rule with the
 * release page and the Releases list (`daysUntilRelease`: the day of the viewer, QA 2026-10-28,
 * lit-9: two pages gave two day counts for one date).
 */
export function daysToRelease(releaseDate: string | null, now: Date): number | null {
  return daysUntilRelease(releaseDate, now);
}

/** The themes of a format that the workspace sees: the AI themes only with `canUseAiReels` (D17). */
export function visibleThemes(format: StudioFormat, canUseAiReels: boolean): StudioTheme[] {
  return themesOfFormat(format).filter((theme) => canUseAiReels || !theme.needsAiFootage);
}

export function themeState(theme: StudioTheme, song: StudioSongContext): StudioThemeState {
  // The music video card opens its editor (spec 2026-10-09 D7, D11): every plan edits it, and the
  // editor says what the song lacks (no master: frame e6). Only "Make the video" there needs Pro.
  if (theme.id === 'musicVideo') {
    return 'open';
  }
  if (needsFullVideoPlan(theme.id) && song.canRenderFullSongVideos === false) {
    return 'needsPlan';
  }
  if (theme.id === 'countdown' && (song.daysToRelease === null || song.daysToRelease < 1)) {
    return 'needsReleaseDate';
  }
  if (theme.needsMaster && !song.masterReady) {
    return 'needsMaster';
  }
  if (theme.needsLyrics && !song.hasLyrics) {
    return 'needsLyrics';
  }
  return 'open';
}

/** True when a card in this state opens step 3: an open card, and a full video on Free (design s7c). */
export function opensDetails(state: StudioThemeState): boolean {
  return state === 'open' || state === 'needsPlan';
}

/** True when a card in this state has a note under it (design s7c): the lyrics, the plan. */
export function stateHasNote(state: StudioThemeState): boolean {
  return state === 'needsLyrics' || state === 'needsPlan';
}

/** The asset types whose open card says "Ready to make" (design s7c). */
export function showsReadyChip(themeId: StudioThemeId): boolean {
  return (
    themeId === 'lyricVideo' ||
    themeId === 'visualizer' ||
    themeId === 'musicVideo' ||
    themeId === 'lyricMoment' ||
    themeId === 'motionArtwork'
  );
}

/** The section of a short theme (design l2): the service loops sit apart from the social reels. */
export function shortSection(theme: StudioTheme): 'social' | 'loops' {
  return theme.family === 'serviceLoops' ? 'loops' : 'social';
}

export interface MomentOption {
  moment: StudioMoment;
  disabled: boolean;
}

/**
 * The moment chips of a theme (designs n1, n3). "Pre-save" only before the release, "Announce"
 * only 7 or more days before it, "A lyric line" off when the catalog has no lyrics.
 */
export function momentOptions(themeId: StudioThemeId, song: StudioSongContext): MomentOption[] {
  const days = song.daysToRelease;
  const before = days !== null && days > 0;
  const announce = days !== null && days >= COUNTDOWN_MIN_DAYS;
  const options: MomentOption[] = [];
  if (themeId === 'story') {
    if (before) {
      options.push({ moment: 'preSave', disabled: false });
    }
    options.push({ moment: 'outNow', disabled: false });
  }
  if (themeId === 'story') {
    options.push({ moment: 'lyricLine', disabled: !song.hasLyrics });
    // "Countdown" (design studio-details a1): the 7, 3 and 1 day Story files. It needs 7 days or
    // more to the release; with less the tile stays and says why.
    options.push({ moment: 'announce', disabled: !announce });
  }
  if (themeId === 'feedPost') {
    // The four moments of the feed post (design `asset-redesign/e Feed post - new`): the Story
    // moments, and "Announce" ("New single" and the date) from 7 days before the release.
    if (before) {
      options.push({ moment: 'preSave', disabled: false });
    }
    options.push({ moment: 'outNow', disabled: false });
    options.push({ moment: 'lyricLine', disabled: !song.hasLyrics });
    if (announce) {
      options.push({ moment: 'announce', disabled: false });
    }
  }
  return options;
}

/** The countdown days still to come (design n3b): 7, 3 and 1, less the ones already gone. */
export function countdownDays(days: number | null): Array<7 | 3 | 1> {
  if (days === null) {
    return [];
  }
  return COUNTDOWN_DAYS.filter((day) => day <= days);
}

/** The first value of each step-3 field when the artist picks a theme. */
export function defaultDetails(themeId: StudioThemeId, song: StudioSongContext): StudioDetails {
  const firstMoment = momentOptions(themeId, song).find((option) => !option.disabled)?.moment;
  switch (themeId) {
    case 'story':
      return { moment: firstMoment };
    case 'feedPost':
      return { moment: firstMoment, shape: '4:5' };
    case 'artworkInMotion':
      // One length and no moment (owner answers 2026-10-08, page q): the pill follows the date.
      return { shape: '9:16' };
    case 'canvasLoop':
      // Four bars of the song (owner answer 2026-10-08): no length choice.
      return {};
    case 'motionArtwork':
      // No length: the server fits whole bars of the song near 20 s (design w). 15, 20 and 30 s override it.
      return {};
    case 'lyricMoment':
      return { lengthSec: 15 };
    case 'spotifyHeader':
      return { headerSource: SPOTIFY_HEADER_DEFAULT_SOURCE };
    case 'banners':
    case 'thumbnail':
    case 'coverSizes':
    case 'lyricCard':
    case 'linkPreview':
    case 'aiReel':
    case 'countdown':
    case 'lyricVideo':
    case 'visualizer':
    case 'musicVideo':
      return {};
  }
}

/**
 * The params of a Spotify header (spec 2026-10-17 §2.2), or null while the picked source has no
 * picture yet (a photo with no file, a bank or AI tile with no picture). Only the keys of the
 * source go out; the focus only for a picture that moves.
 */
export function spotifyHeaderItemParams(details: StudioDetails): KitAssetParams | null {
  const source = details.headerSource ?? SPOTIFY_HEADER_DEFAULT_SOURCE;
  const needs = spotifyHeaderSourceNeeds(source);
  if (needs === 'file' && details.headerFileId === undefined) {
    return null;
  }
  if (needs === 'picture' && details.headerPictureUuid === undefined) {
    return null;
  }
  if (needs === null) {
    return { headerSource: source };
  }
  return {
    headerSource: source,
    ...(needs === 'file' ? { headerFileId: details.headerFileId } : { headerPictureUuid: details.headerPictureUuid }),
    focusX: details.focusX ?? SPOTIFY_HEADER_DEFAULT_FOCUS.x,
    focusY: details.focusY ?? SPOTIFY_HEADER_DEFAULT_FOCUS.y,
  };
}

/** The Studio length of the motion artwork, or undefined: whole bars near 20 s (design w). */
export function motionLength(lengthSec: KitAssetParams['lengthSec']): MotionArtworkLengthSec | undefined {
  const lengths: readonly number[] = MOTION_ARTWORK_LENGTHS;
  return lengthSec !== undefined && lengths.includes(lengthSec) ? (lengthSec as MotionArtworkLengthSec) : undefined;
}

/**
 * The params of a full video (design s7): `trim` only when it cuts the song. Null when the trim
 * is not valid for the song (shorter than 30 s, or past the end).
 */
function fullVideoParams(trim: KitAssetTrim | undefined, durationSec: number | null): KitAssetParams | null {
  if (!isTrimmed(trim, durationSec)) {
    return {};
  }
  if (durationSec !== null && !isValidTrim(trim, durationSec)) {
    return null;
  }
  return { trim: { startSec: trim.startSec, endSec: trim.endSec } };
}

/**
 * The items "Make it" posts for one theme (design n1–n3b), or null while a field is missing. The
 * Canvas starts at the hook when the artist did not move the window. The countdown makes one
 * item per day still to come, with `daysToRelease`.
 */
export function buildStudioItems(
  themeId: StudioThemeId,
  details: StudioDetails,
  song: StudioSongContext & { hookStartSec: number | null; durationSec?: number | null }
): CreateKitAssetItem[] | null {
  const { moment, lyricLine, shape, lengthSec, lineFrom, lineTo } = details;
  switch (themeId) {
    case 'aiReel':
      return null;
    case 'story':
      if (moment === undefined || (moment === 'lyricLine' && (lyricLine === undefined || lyricLine === ''))) {
        return null;
      }
      return [{ themeId, params: moment === 'lyricLine' ? { moment, lyricLine } : { moment } }];
    case 'feedPost':
      if (moment === undefined || (moment === 'lyricLine' && (lyricLine === undefined || lyricLine === ''))) {
        return null;
      }
      return [
        {
          themeId,
          params:
            moment === 'lyricLine' ? { moment, lyricLine, shape: shape ?? '4:5' } : { moment, shape: shape ?? '4:5' },
        },
      ];
    case 'lyricCard':
      return lyricLine === undefined || lyricLine === '' ? null : [{ themeId, params: { lyricLine } }];
    case 'artworkInMotion':
      // One length (15 s) and no moment: the server writes the pill from the date (page q).
      return [{ themeId, params: { shape: shape ?? '9:16' } }];
    case 'canvasLoop':
      return [
        {
          themeId,
          // The server makes four bars from the window start (no length, owner answer 2026-10-08).
          params: { windowStartSec: details.windowStartSec ?? song.hookStartSec ?? 0 },
        },
      ];
    case 'countdown': {
      const days = countdownDays(song.daysToRelease);
      return days.length === 0 ? null : days.map((day) => ({ themeId, params: { daysToRelease: day } }));
    }
    case 'lyricMoment':
      return lineFrom === undefined || lineTo === undefined || lineTo < lineFrom
        ? null
        : [{ themeId, params: { lineFrom, lineTo, lengthSec: lengthSec === 30 ? 30 : 15 } }];
    case 'motionArtwork': {
      const motionSec = motionLength(lengthSec);
      return [{ themeId, params: motionSec === undefined ? {} : { lengthSec: motionSec } }];
    }
    case 'lyricVideo':
    case 'visualizer': {
      const params = fullVideoParams(details.trim, song.durationSec ?? null);
      return params === null ? null : [{ themeId, params }];
    }
    case 'musicVideo':
      // The music video editor makes it (spec 2026-10-09 D11); the server refuses a one-shot request.
      return null;
    case 'spotifyHeader': {
      const params = spotifyHeaderItemParams(details);
      return params === null ? null : [{ themeId, params }];
    }
    case 'banners':
    case 'thumbnail':
    case 'coverSizes':
    case 'linkPreview':
      return [{ themeId, params: {} }];
  }
}

/**
 * The style that step 3 shows and sends (design q3b, q3c): the pick of the artist when the asset
 * type offers it, else the default of the song's theme. AI footage needs `canUseAiReels`; without
 * it the theme default takes its place (a default is never AI footage). Null for an asset type with
 * no styles (the cover sizes) and for the AI reel (its option is the waveform of the reel form).
 */
export function resolveStudioStyle(
  releaseThemeId: ReleaseThemeId,
  themeId: StudioThemeId,
  details: StudioDetails,
  canUseAiReels: boolean
): ResolvedAssetStyle | null {
  if (themeId === 'aiReel') {
    return null;
  }
  const picked = details.style === 'aiFootage' && !canUseAiReels ? undefined : details.style;
  const resolved = resolveAssetStyle(releaseThemeId, themeId, { style: picked, waveform: details.waveform });
  if (resolved === null || (resolved.style === 'aiFootage' && !canUseAiReels)) {
    const fallback = defaultStyleFor(releaseThemeId, themeId);
    return fallback === null ? null : resolveAssetStyle(releaseThemeId, themeId, fallback);
  }
  return resolved;
}

/** The items with the style on each one, so the asset is made in the style that the step shows. */
export function withStudioStyle(
  items: CreateKitAssetItem[] | null,
  style: ResolvedAssetStyle | null
): CreateKitAssetItem[] | null {
  if (items === null || style === null) {
    return items;
  }
  return items.map((item) => ({
    ...item,
    params: {
      ...item.params,
      style: style.style,
      ...(style.waveform === null ? {} : { waveform: style.waveform }),
    },
  }));
}

/**
 * The video credits the items use, by the credit rule of each asset type (`runCredits`): the theme
 * credits for each video file, or one charge for the run (the motion artwork pair). Images are 0.
 * One quality per asset (owner decision 2026-10-08): no item costs more for its quality.
 */
export function itemsCredits(items: CreateKitAssetItem[]): number {
  return items.reduce((sum, item) => {
    const outputs = themeOutputs(item.themeId, { moment: item.params.moment, shape: item.params.shape });
    // A countdown item with a day makes one video; any other item makes the outputs of its theme.
    const files = item.params.daysToRelease === undefined ? outputs : outputs.slice(0, 1);
    return sum + runCredits(item.themeId, files);
  }, 0);
}

/**
 * The default Instructions of the asset in step 3: the text that the director would use for the
 * asset type in the theme, the style and the moment of the step (`assetInstructions.ts`, the same
 * function as music24-api). `lyricsSynced` is true when the lyric timing of the song is `synced`:
 * the server and the Studio catalog use the same rule, so the field shows the server default.
 */
export function studioDefaultInstructions(
  releaseThemeId: ReleaseThemeId,
  themeId: StudioThemeId,
  details: StudioDetails,
  style: ResolvedAssetStyle | null,
  lyricsSynced = false
): string {
  return defaultAssetInstructions({
    assetTypeId: themeId,
    releaseThemeId,
    style: style?.style ?? null,
    waveform: style?.waveform ?? null,
    moment: details.moment,
    lyricsSynced,
  });
}

/**
 * The items with the Instructions of the field on each one; an item whose director does not read
 * them (an image, a lyric video that code lays out: `assetUsesInstructions`) gets none. The server
 * stores only an edited text.
 */
export function withStudioInstructions(
  items: CreateKitAssetItem[] | null,
  instructions: string,
  releaseThemeId: ReleaseThemeId
): CreateKitAssetItem[] | null {
  if (items === null) {
    return null;
  }
  // The server ignores them too (owner decision 2026-10-08, design y3).
  return items.map((item) =>
    assetUsesInstructions({ assetTypeId: item.themeId, releaseThemeId, style: item.params.style })
      ? { ...item, params: { ...item.params, instructions } }
      : item
  );
}

/**
 * The credits of step 3: the credits of the items, or the credits of the asset type while a field
 * is missing (no items yet).
 */
export function studioCredits(themeId: StudioThemeId, items: CreateKitAssetItem[] | null): number {
  return items === null ? STUDIO_THEMES[themeId].credits : itemsCredits(items);
}
